import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as svc from './cobros-service';
import { cobrosKeys } from './keys';
import { clientesKeys } from '../../clientes/api/keys';
import { cobroGeneracionKeys } from './keys';
import { COBRO_GENERACION_ESTADO } from '@aid/shared';
import type {
  CreateCobroDto,
  UpdateCobroDto,
  QueryCobrosDto,
  GenerarCobrosDto,
  AnularCobroDto,
  ReemitirCobroDto,
  QueryCobroGeneracionesDto,
} from './cobros-service';

export interface DeleteCobroVariables {
  id: number;
  idCliente: number;
}

export interface ReemitirCobroVariables {
  id: number;
  idCliente: number;
  dto: ReemitirCobroDto;
}

function invalidateClienteFinancialData(
  queryClient: ReturnType<typeof useQueryClient>,
  idCliente: number,
): void {
  void queryClient.invalidateQueries({ queryKey: clientesKeys.detail(idCliente) });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.cobros(idCliente) });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(idCliente) });
}

export function useCobrosList(params: QueryCobrosDto) {
  return useQuery({
    // SAFETY: QueryKey shape is stable; the cast satisfies react-query's Record<string, unknown> param type.
    queryKey: cobrosKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getCobros(params),
    staleTime: 30_000,
  });
}

interface UseCobrosStatsOptions {
  enabled?: boolean;
}

export function useCobrosStats(
  params: Omit<QueryCobrosDto, 'offset' | 'limit'>,
  options?: UseCobrosStatsOptions,
) {
  return useQuery({
    // SAFETY: QueryKey shape is stable; the cast satisfies react-query's Record<string, unknown> param type.
    queryKey: cobrosKeys.stats(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getCobrosStats(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useCobro(id: number) {
  return useQuery({
    queryKey: cobrosKeys.detail(id),
    queryFn: () => svc.getCobro(id),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

export function useCreateCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCobroDto) => svc.createCobro(dto),
    onSuccess: (data, variables) => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      invalidateClienteFinancialData(qc, data.idCliente ?? variables.idCliente);
    },
  });
}

export function useUpdateCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateCobroDto }) => svc.updateCobro(id, dto),
    onSuccess: (data, variables) => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.detail(variables.id) });
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      invalidateClienteFinancialData(qc, data.idCliente);
    },
  });
}

export function useAnularCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: AnularCobroDto }) => svc.anularCobro(id, dto),
    onSuccess: (data, variables) => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.detail(variables.id) });
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      invalidateClienteFinancialData(qc, data.idCliente);
    },
  });
}

export function useReemitirCobroPreview() {
  return useMutation({
    mutationFn: (id: number) => svc.reemitirCobroPreview(id),
  });
}

export function useReemitirCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: ReemitirCobroVariables) => svc.reemitirCobro(id, dto),
    onSuccess: (_data, variables) => {
      // Re-issue annuls the old invoice and inserts a replacement; refresh the
      // old invoice detail, the lists, and the owning client's financials.
      void qc.invalidateQueries({ queryKey: cobrosKeys.detail(variables.id) });
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      invalidateClienteFinancialData(qc, variables.idCliente);
    },
  });
}

export function useDeleteCobro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: DeleteCobroVariables) => svc.deleteCobro(id),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      invalidateClienteFinancialData(qc, variables.idCliente);
    },
  });
}

export function useGenerarCobros() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: GenerarCobrosDto) => svc.generarCobros(dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      void qc.invalidateQueries({ queryKey: clientesKeys.all });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: cobroGeneracionKeys.all });
    },
  });
}

export function useRecuperarCobros() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => svc.recuperarCobros(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cobrosKeys.lists() });
      void qc.invalidateQueries({ queryKey: clientesKeys.all });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: cobroGeneracionKeys.all });
    },
  });
}

export function useCobrosGeneracionesList(params: QueryCobroGeneracionesDto) {
  return useQuery({
    // SAFETY: QueryKey shape is stable; the cast satisfies react-query's Record<string, unknown> param type.
    queryKey: cobroGeneracionKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getCobrosGeneraciones(params),
    staleTime: 10_000,
    refetchInterval: (query) =>
      query.state.data?.data.some((row) => row.estado === COBRO_GENERACION_ESTADO.EN_PROGRESO)
        ? 2_000
        : false,
  });
}

export function useCobroGeneracionSchedule() {
  return useQuery({
    queryKey: cobroGeneracionKeys.schedule(),
    queryFn: () => svc.getCobroGeneracionSchedule(),
    staleTime: 10_000,
    refetchInterval: (query) =>
      query.state.data?.activeExecution?.estado === COBRO_GENERACION_ESTADO.EN_PROGRESO
        ? 2_000
        : false,
  });
}

export function useCobroGeneracion(id: number) {
  return useQuery({
    queryKey: cobroGeneracionKeys.detail(id),
    queryFn: () => svc.getCobroGeneracion(id),
    enabled: id > 0,
    staleTime: 10_000,
    refetchInterval: (query) =>
      query.state.data?.estado === COBRO_GENERACION_ESTADO.EN_PROGRESO ? 2_000 : false,
  });
}
